/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Alert, AlertDescription } from "../ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Badge } from "../ui/badge";
import { RefreshCw, Save, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import ImageSelector from "./ImageSelector";

const SystemConfig = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState({});
  const [categories, setCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // Configuration section definitions with user-friendly labels
  const sectionDefinitions = {
    branding: {
      title: "School & Branding",
      description: "Customize school identity and visual appearance",
      icon: "🏫",
      fields: {
        school_name: { label: "School Name", type: "text", placeholder: "Enter school name" },
        school_logo_url: { label: "School Logo URL", type: "text", placeholder: "Upload logo and enter URL", description: "Upload a logo image from the gallery below" },
        primary_color: { label: "Primary Color", type: "color", placeholder: "#3b82f6", description: "Used by the 'Custom' theme - select Custom in the theme dropdown to apply" },
        secondary_color: { label: "Secondary Color", type: "color", placeholder: "#64748b", description: "Used by the 'Custom' theme - select Custom in the theme dropdown to apply" },
        contact_email: { label: "Contact Email", type: "email", placeholder: "admin@school.edu" },
        contact_phone: { label: "Contact Phone", type: "tel", placeholder: "(555) 123-4567" },
        school_address: { label: "School Address", type: "textarea", placeholder: "Enter full school address" }
      }
    },
    academic: {
      title: "Academic Calendar",
      description: "Manage semester dates and academic periods",
      icon: "📅",
      fields: {
        current_semester_start: { label: "Current Semester Start", type: "date" },
        current_semester_end: { label: "Current Semester End", type: "date" },
        semester_name: { label: "Semester Name", type: "text", placeholder: "Fall 2025" },
        academic_year: { label: "Academic Year", type: "text", placeholder: "2025-2026" },
        holiday_periods: { label: "Holiday Periods", type: "json", placeholder: "JSON array of holiday periods" }
      }
    },
    testing: {
      title: "Test System Defaults",
      description: "Default settings for new tests and assessments",
      icon: "📝",
      fields: {
        default_pass_threshold: { label: "Default Pass Threshold (%)", type: "number", min: 0, max: 100, step: 1 },
        default_time_limit: { label: "Default Time Limit (minutes)", type: "number", min: 1, max: 300 },
        default_max_retries: { label: "Default Max Retries", type: "number", min: 0, max: 10 },
        default_retry_cooldown: { label: "Retry Cooldown (minutes)", type: "number", min: 0, max: 60 },
        question_randomization: { label: "Question Randomization", type: "switch" },
        answer_randomization: { label: "Answer Randomization", type: "switch" }
      }
    },
    security: {
      title: "Security & Access",
      description: "Authentication and security policies",
      icon: "🔐",
      fields: {
        // Student PIN settings
        min_pin_length: { label: "Minimum PIN Length", type: "number", min: 4, max: 8, description: "PIN length for student accounts" },
        pin_reset_rate_limit: { label: "PIN Reset Rate Limit (/hour)", type: "number", min: 1, max: 20 },
        // Staff password settings
        password_min_length: { label: "Staff Password Min Length", type: "number", min: 8, max: 32, description: "Minimum password length for teacher/admin accounts" },
        password_require_uppercase: { label: "Require Uppercase Letter", type: "switch", description: "Staff passwords must contain A-Z" },
        password_require_lowercase: { label: "Require Lowercase Letter", type: "switch", description: "Staff passwords must contain a-z" },
        password_require_number: { label: "Require Number", type: "switch", description: "Staff passwords must contain 0-9" },
        password_require_symbol: { label: "Require Special Character", type: "switch", description: "Staff passwords must contain !@#$%..." },
        // Email domain restrictions
        allowed_email_domains: { label: "Allowed Email Domains", type: "text", placeholder: "school.edu, district.org", description: "Comma-separated list of allowed domains (empty = all allowed)" },
        staff_email_domain_required: { label: "Enforce Domain Restrictions", type: "switch", description: "Require staff emails to match allowed domains" },
        // Login security
        session_timeout: { label: "Session Timeout (minutes)", type: "number", min: 15, max: 480 },
        max_login_attempts: { label: "Max Login Attempts", type: "number", min: 3, max: 10 },
        account_lockout_duration: { label: "Account Lockout (minutes)", type: "number", min: 5, max: 180 },
        login_attempt_logging: { label: "Login Attempt Logging", type: "switch" },
        ip_rate_limit_enabled: { label: "IP Rate Limiting Enabled", type: "switch" },
        ip_rate_limit_max: { label: "IP Rate Limit Max Attempts", type: "number", min: 10, max: 1000 },
        ip_rate_limit_window: { label: "IP Rate Limit Window (minutes)", type: "number", min: 5, max: 60 },
        ip_rate_limit_alert_threshold: { label: "IP Activity Alert Threshold", type: "number", min: 5, max: 100 }
      }
    },
    email: {
      title: "Email & Notifications",
      description: "SMTP configuration and notification settings",
      icon: "📧",
      fields: {
        smtp_host: { label: "SMTP Host", type: "text", placeholder: "smtp.gmail.com" },
        smtp_port: { label: "SMTP Port", type: "number", min: 1, max: 65535 },
        smtp_secure: { label: "Secure Connection", type: "switch" },
        smtp_user: { label: "SMTP Username", type: "text" },
        smtp_password: { label: "SMTP Password", type: "password" },
        from_email: { label: "From Email Address", type: "email" },
        admin_notification_emails: { label: "Admin Notification Recipients", type: "json", placeholder: "JSON array of email addresses" }
      }
    },
    system: {
      title: "System Limits & Performance",
      description: "File uploads, database, and performance settings",
      icon: "⚙️",
      fields: {
        max_file_upload_size: { label: "Max File Upload Size (MB)", type: "number", min: 1, max: 50, step: 1, description: "Maximum file size for image uploads in megabytes" },
        supported_image_formats: { label: "Supported Image Formats", type: "json", placeholder: "JSON array of formats" },
        db_connection_pool_size: { label: "DB Connection Pool Size", type: "number", min: 5, max: 50 },
        log_level: { label: "Log Level", type: "select", options: ["error", "warn", "info", "debug"] }
      }
    },
    analytics: {
      title: "Analytics & Reporting",
      description: "Data collection and reporting configuration",
      icon: "📊",
      fields: {
        google_analytics_id: { label: "Google Analytics ID", type: "text", placeholder: "G-XXXXXXXXXX" },
        data_retention_months: { label: "Data Retention (months)", type: "number", min: 6, max: 120 },
        anonymous_usage_stats: { label: "Anonymous Usage Statistics", type: "switch" },
        export_formats: { label: "Available Export Formats", type: "json", placeholder: "JSON array of formats" }
      }
    },
    tenancy: {
      title: "Multi-Tenancy",
      description: "Multi-school deployment settings",
      icon: "🏢",
      fields: {
        school_district_id: { label: "School District ID", type: "text", placeholder: "Unique district identifier" },
        data_isolation_level: { label: "Data Isolation Level", type: "select", options: ["strict", "moderate", "shared"] },
        shared_resource_access: { label: "Shared Resource Access", type: "select", options: ["none", "district", "province"] },
        support_contact_method: { label: "Support Contact Method", type: "select", options: ["email", "phone", "ticket"] }
      }
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchCategories();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-config`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }

      const data = await response.json();
      setConfig(data.config);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-config/categories`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (err) {
      console.warn('Failed to fetch categories:', err);
    }
  };

  const handleFieldChange = (category, field, value) => {
    const changeKey = `${category}.${field}`;

    setChanges(prev => ({
      ...prev,
      [changeKey]: value
    }));

    // Clear validation error when user makes changes
    if (validationErrors[changeKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[changeKey];
        return newErrors;
      });
    }

    setSuccess(''); // Clear success message when making changes
  };

  const saveChanges = async (category) => {
    try {
      setSaving(true);
      setError('');

      // Get changes for this category only
      const categoryChanges = {};
      Object.entries(changes).forEach(([key, value]) => {
        if (key.startsWith(`${category}.`)) {
          const field = key.substring(category.length + 1);
          // Special case: Convert MB to bytes for file size before saving
          if (field === 'max_file_upload_size' && value) {
            categoryChanges[field] = Number(value) * 1024 * 1024;
          } else {
            categoryChanges[field] = value;
          }
        }
      });

      if (Object.keys(categoryChanges).length === 0) {
        setError('No changes to save');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          category,
          updates: categoryChanges,
          changeReason: `Updated ${category} settings via admin panel`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      const data = await response.json();
      setSuccess(`Successfully updated ${data.updatedConfigs.length} setting(s) in ${category}`);

      // Clear changes for this category
      const newChanges = { ...changes };
      Object.keys(changes).forEach(key => {
        if (key.startsWith(`${category}.`)) {
          delete newChanges[key];
        }
      });
      setChanges(newChanges);

      // Refresh configuration
      await fetchConfig();

      // If security config was changed, reload rate limiter
      if (category === 'security') {
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/api/admin/config/reload-rate-limiter`, {
            method: 'POST',
            credentials: 'include'
          });
        } catch (reloadErr) {
          console.error('Failed to reload rate limiter:', reloadErr);
          // Don't fail the entire save if reload fails
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (category, fieldName, fieldConfig, currentValue) => {
    const changeKey = `${category}.${fieldName}`;
    let displayValue = changes[changeKey] !== undefined ? changes[changeKey] : currentValue;
    const hasChanges = changes[changeKey] !== undefined;
    const errors = validationErrors[changeKey];

    // Special case: Convert bytes to MB for file size display
    if (fieldName === 'max_file_upload_size' && displayValue && !hasChanges) {
      displayValue = Math.round(displayValue / (1024 * 1024));
    }

    const commonProps = {
      id: `${category}-${fieldName}`,
      value: displayValue || '',
      onChange: (e) => {
        let value = e.target.value;
        // Special case: Convert MB to bytes when saving file size
        if (fieldName === 'max_file_upload_size' && value) {
          value = Number(value);
        }
        handleFieldChange(category, fieldName, value);
      },
      className: hasChanges ? 'border-orange-300 bg-orange-50' : ''
    };

    const renderInput = () => {
      // Special case for logo selector
      if (fieldName === 'school_logo_url') {
        return (
          <ImageSelector
            currentImageUrl={displayValue}
            onImageSelect={(imageUrl) => handleFieldChange(category, fieldName, imageUrl)}
            onImageRemove={() => handleFieldChange(category, fieldName, '')}
            disabled={false}
          />
        );
      }

      switch (fieldConfig.type) {
        case 'switch':
          return (
            <div className="flex items-center space-x-2">
              <Switch
                id={commonProps.id}
                checked={displayValue === true || displayValue === 'true'}
                onCheckedChange={(checked) => handleFieldChange(category, fieldName, checked)}
                className={hasChanges ? 'data-[state=checked]:bg-orange-500' : ''}
              />
              <Label htmlFor={commonProps.id} className="text-sm text-gray-600">
                {displayValue === true || displayValue === 'true' ? 'Enabled' : 'Disabled'}
              </Label>
            </div>
          );

        case 'textarea':
          return (
            <Textarea
              {...commonProps}
              placeholder={fieldConfig.placeholder}
              rows={3}
            />
          );

        case 'select':
          return (
            <select
              {...commonProps}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${commonProps.className}`}
            >
              {fieldConfig.options?.map(option => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
          );

        case 'color':
          return (
            <div className="flex items-center space-x-2">
              <input
                type="color"
                {...commonProps}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <Input
                {...commonProps}
                placeholder={fieldConfig.placeholder}
                className="flex-1"
              />
            </div>
          );

        default:
          return (
            <Input
              {...commonProps}
              type={fieldConfig.type || 'text'}
              placeholder={fieldConfig.placeholder}
              min={fieldConfig.min}
              max={fieldConfig.max}
              step={fieldConfig.step}
            />
          );
      }
    };

    return (
      <div key={fieldName} className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={commonProps.id} className="text-sm font-medium">
            {fieldConfig.label}
            {config[category]?.[fieldName]?.isRequired && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </Label>
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Modified
            </Badge>
          )}
        </div>

        {renderInput()}

        {config[category]?.[fieldName]?.description && (
          <p className="text-xs text-gray-500">
            {config[category][fieldName].description}
          </p>
        )}

        {errors && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {Array.isArray(errors) ? errors.join(', ') : errors}
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  const renderCategoryCard = (categoryName, sectionDef) => {
    const categoryData = config[categoryName] || {};
    const categoryInfo = categories.find(c => c.name === categoryName);
    const hasChanges = Object.keys(changes).some(key => key.startsWith(`${categoryName}.`));

    return (
      <AccordionItem key={categoryName} value={categoryName}>
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center justify-between w-full mr-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{sectionDef.icon}</span>
              <div className="text-left">
                <h3 className="font-semibold">{sectionDef.title}</h3>
                <p className="text-sm text-gray-600">{sectionDef.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {hasChanges && (
                <Badge variant="outline" className="text-orange-600 border-orange-300">
                  {Object.keys(changes).filter(key => key.startsWith(`${categoryName}.`)).length} changes
                </Badge>
              )}
              {categoryInfo && (
                <div className="text-right text-xs text-gray-500">
                  <div>{categoryInfo.settingCount} settings</div>
                  {categoryInfo.lastUpdated && (
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(categoryInfo.lastUpdated).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </AccordionTrigger>

        <AccordionContent>
          <div className="space-y-6 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(sectionDef.fields).map(([fieldName, fieldConfig]) =>
                renderField(categoryName, fieldName, fieldConfig, categoryData[fieldName]?.value)
              )}
            </div>

            {hasChanges && (
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => saveChanges(categoryName)}
                  disabled={saving}
                  className="flex items-center space-x-2"
                >
                  {saving ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>Save {categoryName.charAt(0).toUpperCase() + categoryName.slice(1)} Settings</span>
                </Button>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-lg">Loading system configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <span>⚙️</span>
                <span>System Configuration</span>
              </CardTitle>
              <CardDescription>
                Comprehensive system settings for multi-school deployment management
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={fetchConfig}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          <Accordion type="single" collapsible className="space-y-4">
            {Object.entries(sectionDefinitions).map(([categoryName, sectionDef]) =>
              renderCategoryCard(categoryName, sectionDef)
            )}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemConfig;